import type { StaffUser } from '@/types/auth.types';
import { isTzRules } from '@/lib/tzRules';

export function mapStaffAuthUser(serverUser: unknown): StaffUser {
  const user = (serverUser ?? {}) as Record<string, unknown>;

  // IANA name resolved by the API (users_timezones with fallback to
  // aa_accounts.timezone). When omitted (older payloads), let consumers
  // fall back to the browser's resolvedOptions().timeZone so we never
  // render dates against an undefined zone.
  const timezone = typeof user.timezone === 'string' && user.timezone !== ''
    ? user.timezone
    : undefined;

  const rawRole = typeof user.role === 'string' && user.role.trim() !== ''
    ? user.role.trim()
    : null;
  const rawRoleTitle = typeof user.roleTitle === 'string' && user.roleTitle.trim() !== ''
    ? user.roleTitle.trim()
    : null;

  return {
    id: Number(user.userID ?? user.id ?? 0),
    accountId: Number(user.accountId ?? user.accountID ?? 0),
    username: String(user.userName ?? user.username ?? user.email ?? ''),
    firstName: String(user.firstName ?? ''),
    lastName: String(user.lastName ?? ''),
    email: String(user.email ?? ''),
    userLevel: Number(user.userLevel ?? 0) as StaffUser['userLevel'],
    role: rawRole,
    roleTitle: rawRoleTitle,
    displayName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    avatarUrl: null,
    facilityIds: [],
    isActive: true,
    createdAt: String(user.createdAt ?? new Date().toISOString()),
    updatedAt: String(user.updatedAt ?? new Date().toISOString()),
    timezone,
    // The server's offset rules, so the first page after sign-in converts
    // with them rather than the browser's copy (SCRUM-2428).
    tzRules: isTzRules(user.tzRules) ? user.tzRules : undefined,
    // Corp identity flags. Carried here so a fresh login has them before the
    // /auth/me refresh lands — without this, useAuth() falls back to
    // userLevel >= 5 for isSuperAdmin on the first render after login.
    masterUserId: typeof user.masterUserId === 'number' ? user.masterUserId : undefined,
    isMasterAdmin: user.isMasterAdmin === true,
    isSuperAdmin: user.isSuperAdmin === true,
    permissions: Array.isArray(user.permissions)
      ? (user.permissions as unknown[]).map(Number).filter((n) => !Number.isNaN(n))
      : undefined,
  };
}
